import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, UserPlus, CheckCircle2, TrendingUp, Trophy, XCircle, Clock, Megaphone, FileText, Mail,
} from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const CARDS = [
  { key: "total_leads", label: "Total Leads", icon: Users, color: "text-blue-400" },
  { key: "new_leads", label: "New Leads", icon: UserPlus, color: "text-cyan-400" },
  { key: "qualified_leads", label: "Qualified", icon: CheckCircle2, color: "text-violet-400" },
  { key: "conversion_rate", label: "Conversion Rate", icon: TrendingUp, color: "text-green-400", suffix: "%" },
  { key: "won_leads", label: "Won", icon: Trophy, color: "text-emerald-400" },
  { key: "lost_leads", label: "Lost", icon: XCircle, color: "text-red-400" },
  { key: "followups_due", label: "Follow-ups Due", icon: Clock, color: "text-amber-400" },
  { key: "total_campaigns", label: "Campaigns", icon: Megaphone, color: "text-pink-400" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <SEO title="Admin Dashboard | NewStarAds" />
      <h1 className="font-display text-2xl font-bold tracking-tight">Dashboard</h1>
      <p className="text-slate-400 text-sm">Overview of leads, conversions and activity.</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-xl border border-white/10 bg-white/5 p-5">
            <c.icon className={`w-5 h-5 ${c.color}`} strokeWidth={1.5} />
            <p className="mt-3 font-display text-3xl font-black tracking-tight">{stats ? stats[c.key] : "—"}{c.suffix && stats ? c.suffix : ""}</p>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/10 bg-white/5 p-6">
          <h2 className="font-display font-bold mb-4">Leads by status</h2>
          {stats && Object.entries(stats.by_status).map(([k, v]) => {
            const max = Math.max(...Object.values(stats.by_status), 1);
            return (
              <div key={k} className="mb-3">
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{k}</span><span className="text-slate-500">{v}</span></div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(v / max) * 100}%` }} /></div>
              </div>
            );
          })}
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-6">
          <h2 className="font-display font-bold mb-4">Leads by source</h2>
          {stats && Object.entries(stats.by_source).length === 0 && <p className="text-slate-500 text-sm">No data yet.</p>}
          {stats && Object.entries(stats.by_source).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-slate-300">{k}</span><span className="text-slate-400">{v}</span></div>
          ))}
          <div className="mt-4 flex gap-4 text-sm text-slate-400">
            <Link to="/admin/inbox" className="inline-flex items-center gap-1 hover:text-white"><Mail className="w-4 h-4" /> {stats?.unread_contacts || 0} unread</Link>
            <Link to="/admin/blog" className="inline-flex items-center gap-1 hover:text-white"><FileText className="w-4 h-4" /> {stats?.total_posts || 0} posts</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
